// src/core/services/layers.js
import L from "leaflet";
import { PMTiles, leafletRasterLayer } from "pmtiles";
import * as EL from "esri-leaflet";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import proj4 from "proj4";

if (typeof globalThis !== "undefined" && !globalThis.proj4) {
  globalThis.proj4 = proj4;
}

export function createLayerService({ events, ui, map }) {
  const LAYERS_KEY = "hydrotools:layers:v1";
  const stack = []; // ordered bottom -> top

  function _snapshot() {
    return stack.map((rec) => ({
      id: rec.id,
      toolId: rec.toolId,
      name: rec.name,
      group: rec.group ?? null,
      kind: rec.kind,
      type: rec.type,
      url: rec.url,
      options: rec.options ?? {},
      opacity: rec.opacity,
      visible: rec.visible,
      meta: rec.meta ?? {},
      volatile: !!rec.volatile,
    }));
  }

  function _persist() {
    try {
      const persistable = _snapshot().filter((x) => !x.volatile);
      localStorage.setItem(LAYERS_KEY, JSON.stringify(persistable));
    } catch (err) {
      console.error("[Layers] persist failed", err);
    }
  }

  function _applyStacking() {
    // Leaflet draws in add order; bringToFront ensures correct ordering
    for (const rec of stack) {
      if (!rec.visible) continue;
      if (rec.type === "managed") {
        rec.managed?.bringToFront?.();
        continue;
      }
      if (rec.leaflet?.bringToFront) rec.leaflet.bringToFront();
      if (rec._raster?.bringToFront) rec._raster.bringToFront();
      if (rec.leaflet?.eachLayer) {
        rec.leaflet.eachLayer((child) => {
          if (child?.bringToFront) child.bringToFront();
        });
      }
    }
    _persist();
    events.emit("layers:changed", listLayers());
  }

  function addLayer(toolId, def) {
    const id = def.id ?? `layer-${crypto.randomUUID?.() ?? Math.random().toString(16).slice(2)}`;

    let rec;
    let leaflet;
    if (def.type === "xyz") {
      leaflet = L.tileLayer(def.url, {
        crossOrigin: true,
        ...(def.options ?? {}),
      });
    } else if (def.type === "wms") {
      const wmsOptions = { crossOrigin: true, ...(def.options ?? {}) };
      const crsCode = String(wmsOptions.crsCode || "").toUpperCase();
      if (crsCode === "EPSG:4326") wmsOptions.crs = L.CRS.EPSG4326;
      else if (crsCode === "EPSG:3857") wmsOptions.crs = L.CRS.EPSG3857;
      delete wmsOptions.crsCode;
      leaflet = L.tileLayer.wms(def.url, wmsOptions);
    } else if (def.type === "esri-image") {
      leaflet = EL.imageMapLayer({ url: def.url, ...(def.options ?? {}) });
    } else if (def.type === "esri-feature") {
      const featureOptions = { ...(def.options ?? {}) };
      if (featureOptions.interactive == null) featureOptions.interactive = true;
      const styleBuilder = typeof featureOptions.styleBuilder === "function"
        ? featureOptions.styleBuilder
        : null;
      const popupBuilder = typeof featureOptions.popupBuilder === "function"
        ? featureOptions.popupBuilder
        : null;
      delete featureOptions.styleBuilder;
      delete featureOptions.popupBuilder;
      leaflet = EL.featureLayer({
        url: def.url,
        ...featureOptions,
        style: (feature) => buildEsriFeatureStyle(feature, def.opacity, styleBuilder),
        onEachFeature: popupBuilder
          ? (feature, layer) => {
              const popupHtml = popupBuilder(feature);
              if (popupHtml) layer.bindPopup(popupHtml, { maxWidth: 360 });
            }
          : undefined,
      });
      if (popupBuilder && leaflet?.bindPopup) {
        try {
          leaflet.bindPopup((layer) => {
            const popupHtml = popupBuilder(layer?.feature || layer);
            return popupHtml || "";
          }, { maxWidth: 360 });
        } catch {}
      }
    } else if (def.type === "pmtiles-raster") {
      const source = new PMTiles(def.url);
      leaflet = leafletRasterLayer(source, def.options ?? {});
      source.getHeader()
        .then((h) => {
          const bounds = L.latLngBounds(
            [h.minLat, h.minLon],
            [h.maxLat, h.maxLon],
          );
          if (!bounds.isValid()) return;
          if (!bounds.contains(map.instance.getCenter())) {
            ui?.toast?.("PMTiles loaded. Pan/zoom to the dataset extent.");
          }
        })
        .catch((err) => {
          console.error("[Layers] PMTiles header load failed", err);
          ui?.toast?.(`PMTiles failed to load: ${def.url}`);
        });
    } else if (def.type === "geotiff-url") {
      leaflet = L.layerGroup();
      const url = def.url;
      console.warn("[Layers Debug] Loading GeoTIFF", def.name || def.url);
      ui?.toast?.(`Loading GeoTIFF: ${def.name || def.url}`);
      const customProjectionCode = String(def?.options?.projectionCode || "").trim();
      const customProjectionDef = String(def?.options?.projectionDef || "").trim();
      const worldFileAffine = parseWorldFileAffine(def?.options?.worldFileAffine);
      const geographicBounds = parseGeographicBounds(def?.options?.geographicBounds);
      const resolvedProjectionCode = customProjectionDef
        ? (customProjectionCode || `LOCAL:GEOTIFF:${id}`)
        : customProjectionCode;
      if (resolvedProjectionCode && customProjectionDef) {
        try {
          if (!proj4.defs(resolvedProjectionCode)) proj4.defs(resolvedProjectionCode, customProjectionDef);
          if (globalThis?.proj4?.defs && !globalThis.proj4.defs(resolvedProjectionCode)) {
            globalThis.proj4.defs(resolvedProjectionCode, customProjectionDef);
          }
        } catch (err) {
          console.warn("[Layers] GeoTIFF projection registration failed", err);
        }
      }
      Promise.resolve()
        .then(() => fetch(url))
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.arrayBuffer();
        })
        .then((ab) => parseGeoraster(ab))
        .then((georaster) => {
          if (!rec) return;
          console.warn("[Layers Debug] Parsed GeoTIFF", rec.name);
          ui?.toast?.(`Parsed GeoTIFF: ${rec.name}`);
          const useGeographicBounds = !!geographicBounds;
          if (useGeographicBounds) {
            georaster.projection = "EPSG:4326";
            georaster.xmin = geographicBounds.west;
            georaster.xmax = geographicBounds.east;
            georaster.ymin = geographicBounds.south;
            georaster.ymax = geographicBounds.north;
            const w = Number(georaster.width);
            const h = Number(georaster.height);
            if (Number.isFinite(w) && w > 0) georaster.pixelWidth = (geographicBounds.east - geographicBounds.west) / w;
            if (Number.isFinite(h) && h > 0) georaster.pixelHeight = (geographicBounds.north - geographicBounds.south) / h;
            console.info("[Layers] Using metadata geographic bounds", geographicBounds);
            // When we have trusted geographic bounds, ignore sidecar projected transforms.
            // This avoids mixing projected world-file coordinates with EPSG:4326 extents.
          } else if (worldFileAffine) {
            applyWorldFileToGeoraster(georaster, worldFileAffine);
          }
          if (!useGeographicBounds && resolvedProjectionCode) {
            georaster.projection = resolvedProjectionCode;
          }
          if (!useGeographicBounds && resolvedProjectionCode && customProjectionDef) {
            const imageBounds = computeProjectedGeoTiffBounds(georaster, resolvedProjectionCode);
            if (!imageBounds?.isValid?.()) {
              throw new Error(`Projected GeoTIFF bounds are invalid for ${rec.name || def.name || def.url}`);
            }
            const rendered = georaster.toCanvas
              ? georaster.toCanvas({
                  width: Number(georaster.width) || undefined,
                  height: Number(georaster.height) || undefined,
                })
              : null;
            return Promise.resolve(rendered).then((canvas) => {
              if (!canvas?.toDataURL) {
                throw new Error("Projected GeoTIFF canvas rendering is unavailable.");
              }
              const overlay = L.imageOverlay(canvas.toDataURL("image/png"), imageBounds, {
                opacity: rec.opacity ?? 1,
                interactive: false,
                crossOrigin: true,
                className: "leaflet-raster-pixelated",
              });
              rec._raster = overlay;
              overlay.addTo(leaflet);
              if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
              if (!imageBounds.contains(map.instance.getCenter())) {
                ui?.toast?.(`GeoTIFF loaded: ${rec.name}`);
              }
            });
          }
          const pxWidth = Number(georaster?.width);
          const pxHeight = Number(georaster?.height);
          const totalPixels = (Number.isFinite(pxWidth) && Number.isFinite(pxHeight)) ? (pxWidth * pxHeight) : 0;
          if (totalPixels > 30_000_000 && georaster?.toCanvas) {
            const previewMaxDim = 2048;
            const scale = previewMaxDim / Math.max(pxWidth || 1, pxHeight || 1);
            const previewWidth = Math.max(1, Math.round((pxWidth || 1) * Math.min(1, scale)));
            const previewHeight = Math.max(1, Math.round((pxHeight || 1) * Math.min(1, scale)));
            const previewBounds = L.latLngBounds(
              [Number(georaster?.ymin), Number(georaster?.xmin)],
              [Number(georaster?.ymax), Number(georaster?.xmax)],
            );
            if (previewBounds?.isValid?.()) {
              console.warn("[Layers Debug] Using large-raster preview mode", { previewWidth, previewHeight, totalPixels });
              ui?.toast?.(`Large GeoTIFF preview mode: ${previewWidth}x${previewHeight}`);
              return Promise.resolve(
                georaster.toCanvas({
                  width: previewWidth,
                  height: previewHeight,
                })
              ).then((canvas) => {
                if (!canvas?.toDataURL) throw new Error("GeoTIFF preview canvas failed.");
                const overlay = L.imageOverlay(canvas.toDataURL("image/png"), previewBounds, {
                  opacity: rec.opacity ?? 1,
                  interactive: false,
                  crossOrigin: true,
                  className: "leaflet-raster-pixelated",
                });
                rec._raster = overlay;
                overlay.addTo(leaflet);
                if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
                if (def?.options?.autoZoom) {
                  try { map.instance.fitBounds(previewBounds.pad?.(0.06) || previewBounds); } catch {}
                }
                return;
              });
            }
          }
          const raster = new GeoRasterLayer({
            georaster,
            proj4,
            opacity: rec.opacity ?? 1,
            resolution: Number(def?.options?.resolution || 256),
            pixelValuesToColorFn: buildGeoTiffColorFn(georaster, def?.options),
          });
          console.warn("[Layers Debug] GeoRasterLayer created", {
            name: rec.name,
            projection: georaster?.projection,
            width: georaster?.width,
            height: georaster?.height,
            xmin: georaster?.xmin,
            xmax: georaster?.xmax,
            ymin: georaster?.ymin,
            ymax: georaster?.ymax,
            pixelWidth: georaster?.pixelWidth,
            pixelHeight: georaster?.pixelHeight,
          });
          rec._raster = raster;
          raster.addTo(leaflet);
          console.warn("[Layers Debug] Raster added to layerGroup", rec.name);
          if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
          console.warn("[Layers Debug] LayerGroup added to map?", {
            visible: rec.visible,
            hasLayer: map.instance.hasLayer(leaflet),
          });
          try {
            const b = raster.getBounds?.();
            console.warn("[Layers Debug] Raster bounds", b);
            if (def?.options?.autoZoom && b?.isValid?.()) {
              try { map.instance.fitBounds(b.pad?.(0.06) || b); } catch {}
            }
            if (b?.isValid?.() && !b.contains(map.instance.getCenter())) {
              ui?.toast?.(`GeoTIFF loaded: ${rec.name}`);
            }
          } catch {}
        })
        .catch((err) => {
          console.error("[Layers] GeoTIFF load failed", err);
          ui?.toast?.(`GeoTIFF failed: ${def.name || def.url} (${String(err?.message || err)})`);
        });
    } else if (def.type === "leaflet-group") {
      leaflet = def.leaflet;
      if (!leaflet) throw new Error("leaflet-group requires def.leaflet");
    } else if (def.type === "managed") {
      leaflet = null;
    } else {
      throw new Error(`Unsupported layer type: ${def.type}`);
    }

    rec = {
      id,
      toolId,
      name: def.name ?? id,
      group: def.group ?? null,
      kind: def.kind ?? "overlay", // "basemap" or "overlay"
      type: def.type,
      url: def.url,
      options: def.options ?? {},
      opacity: def.opacity ?? 1,
      visible: def.visible ?? true,
      meta: def.meta ?? {},
      volatile: !!def.volatile,
      leaflet,
      _raster: null,
      managed: def.managed ?? null,
    };

    stack.push(rec);

    if (rec.type === "managed") {
      if (rec.visible) rec.managed?.setVisible?.(true);
      rec.managed?.setOpacity?.(rec.opacity);
      rec.managed?.setMeta?.(rec.meta ?? {});
    } else {
      if (rec.visible) leaflet.addTo(map.instance);
      leaflet?.setOpacity?.(rec.opacity);
    }

    _applyStacking();
    return id;
  }

  function removeLayer(id) {
    const idx = stack.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const rec = stack[idx];
    if (rec.type === "managed") {
      try { rec.managed?.remove?.(); } catch {}
    } else if (rec.leaflet) map.instance.removeLayer(rec.leaflet);
    if (rec.volatile && typeof rec.url === "string" && rec.url.startsWith("blob:")) {
      try { URL.revokeObjectURL(rec.url); } catch {}
    }
    stack.splice(idx, 1);
    _applyStacking();
  }

  function removeLayersByTool(toolId) {
    const toRemove = stack.filter((r) => r.toolId === toolId).map((r) => r.id);
    for (const id of toRemove) removeLayer(id);
  }

  function setOpacity(id, opacity, options = {}) {
    const emit = options?.emit !== false;
    const persist = options?.persist !== false;
    const rec = stack.find((r) => r.id === id);
    if (!rec) return;
    rec.opacity = Math.max(0, Math.min(1, opacity));
    if (rec.type === "managed") {
      rec.managed?.setOpacity?.(rec.opacity);
    } else {
      if (rec.type === "esri-feature") applyEsriFeatureOpacity(rec);
      else rec.leaflet?.setOpacity?.(rec.opacity);
      rec._raster?.setOpacity?.(rec.opacity);
    }
    if (persist) _persist();
    if (emit) events.emit("layers:changed", listLayers());
  }

  function setName(id, name) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    const next = String(name ?? "").trim();
    if (!next) return false;
    rec.name = next;
    _persist();
    events.emit("layers:changed", listLayers());
    return true;
  }

  function setGroup(id, group) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    rec.group = group ? String(group) : null;
    _persist();
    events.emit("layers:changed", listLayers());
    return true;
  }

  function updateLayer(id, patch = {}) {
    const rec = stack.find((r) => r.id === id);
    if (!rec || !patch || typeof patch !== "object") return false;
    let needsStackApply = false;
    if (Object.prototype.hasOwnProperty.call(patch, "name")) {
      const n = String(patch.name ?? "").trim();
      if (n) rec.name = n;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "group")) {
      rec.group = patch.group ? String(patch.group) : null;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "meta")) {
      rec.meta = { ...(rec.meta ?? {}), ...(patch.meta ?? {}) };
      if (rec.type === "managed") rec.managed?.setMeta?.(rec.meta);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "opacity")) {
      rec.opacity = Math.max(0, Math.min(1, Number(patch.opacity) || 0));
      if (rec.type === "managed") rec.managed?.setOpacity?.(rec.opacity);
      else {
        if (rec.type === "esri-feature") applyEsriFeatureOpacity(rec);
        else rec.leaflet?.setOpacity?.(rec.opacity);
        rec._raster?.setOpacity?.(rec.opacity);
      }
    }
    if (Object.prototype.hasOwnProperty.call(patch, "visible")) {
      rec.visible = !!patch.visible;
      if (rec.type === "managed") rec.managed?.setVisible?.(rec.visible);
      else if (rec.visible) rec.leaflet?.addTo?.(map.instance);
      else if (rec.leaflet) map.instance.removeLayer(rec.leaflet);
      needsStackApply = true;
    }
    if (needsStackApply) _applyStacking();
    else {
      _persist();
      events.emit("layers:changed", listLayers());
    }
    return true;
  }

  function setVisible(id, visible) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return;
    rec.visible = !!visible;
    if (rec.type === "managed") {
      rec.managed?.setVisible?.(rec.visible);
    } else {
      if (rec.visible) rec.leaflet.addTo(map.instance);
      else map.instance.removeLayer(rec.leaflet);
    }
    _applyStacking();
  }

  function move(id, direction) {
    const idx = stack.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const newIdx = direction === "up" ? idx + 1 : idx - 1; // up = toward top
    if (newIdx < 0 || newIdx >= stack.length) return;
    const [rec] = stack.splice(idx, 1);
    stack.splice(newIdx, 0, rec);
    _applyStacking();
  }

  function reorderKind(kind, idsTopToBottom) {
    if (!Array.isArray(idsTopToBottom) || !idsTopToBottom.length) return;
    const wanted = idsTopToBottom
      .map((id) => stack.find((r) => r.id === id && r.kind === kind))
      .filter(Boolean);
    if (!wanted.length) return;
    const replacementBottomToTop = [...wanted].reverse();
    let cursor = 0;
    const next = stack.map((rec) => {
      if (rec.kind !== kind) return rec;
      const repl = replacementBottomToTop[cursor];
      cursor += 1;
      return repl ?? rec;
    });
    stack.splice(0, stack.length, ...next);
    _applyStacking();
  }

  function reorderSubset(idsTopToBottom) {
    if (!Array.isArray(idsTopToBottom) || !idsTopToBottom.length) return;
    const wanted = idsTopToBottom
      .map((id) => stack.find((r) => r.id === id))
      .filter(Boolean);
    if (!wanted.length) return;
    const wantedSet = new Set(wanted.map((r) => r.id));
    const replacementBottomToTop = [...wanted].reverse();
    let cursor = 0;
    const next = stack.map((rec) => {
      if (!wantedSet.has(rec.id)) return rec;
      const repl = replacementBottomToTop[cursor];
      cursor += 1;
      return repl ?? rec;
    });
    stack.splice(0, stack.length, ...next);
    _applyStacking();
  }

  function zoomTo(id) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    const b = rec.type === "managed"
      ? rec.managed?.getBounds?.()
      : (rec.leaflet?.getBounds?.() || rec._raster?.getBounds?.());
    if (!b?.isValid?.()) return false;
    map.instance.fitBounds(b.pad?.(0.12) || b);
    return true;
  }

  function listLayers() {
    // Return bottom->top; UI can render top-first if preferred
    return _snapshot();
  }

  function _restorePersisted() {
    let raw = null;
    try {
      raw = localStorage.getItem(LAYERS_KEY);
    } catch (err) {
      console.error("[Layers] read persisted failed", err);
      return;
    }
    if (!raw) return;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.error("[Layers] parse persisted failed", err);
      return;
    }
    if (!Array.isArray(parsed)) return;

    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      if (!item.type || !item.url) continue;
      try {
        addLayer(item.toolId ?? "overlay-manager", {
          id: item.id,
          name: item.name,
          group: item.group ?? null,
          kind: item.kind ?? "overlay",
          type: item.type,
          url: item.url,
          options: item.options ?? {},
          opacity: Number.isFinite(Number(item.opacity)) ? Number(item.opacity) : 1,
          visible: item.visible !== false,
          meta: item.meta ?? {},
        });
      } catch (err) {
        console.error("[Layers] restore layer failed", item, err);
      }
    }
  }

  _restorePersisted();

  return {
    addLayer,
    removeLayer,
    removeLayersByTool,
    setOpacity,
    setVisible,
    setName,
    setGroup,
    updateLayer,
    move,
    reorderKind,
    reorderSubset,
    zoomTo,
    listLayers,
  };
}

function computeProjectedGeoTiffBounds(georaster, projectionCode) {
  const xmin = Number(georaster?.xmin);
  const xmax = Number(georaster?.xmax);
  const ymin = Number(georaster?.ymin);
  const ymax = Number(georaster?.ymax);
  if (![xmin, xmax, ymin, ymax].every(Number.isFinite)) return null;
  const corners = [
    [xmin, ymin],
    [xmin, ymax],
    [xmax, ymin],
    [xmax, ymax],
  ]
    .map(([x, y]) => {
      const result = proj4(projectionCode, "EPSG:4326", [x, y]);
      const lon = Number(result?.[0]);
      const lat = Number(result?.[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return [lat, lon];
    })
    .filter(Boolean);
  if (!corners.length) return null;
  return L.latLngBounds(corners);
}

function parseWorldFileAffine(value) {
  if (!Array.isArray(value) || value.length < 6) return null;
  const nums = value.slice(0, 6).map((n) => Number(n));
  if (!nums.every((n) => Number.isFinite(n))) return null;
  return nums;
}

function parseGeographicBounds(value) {
  if (!Array.isArray(value) || value.length < 4) return null;
  const nums = value.slice(0, 4).map((n) => Number(n));
  const [west, east, south, north] = nums;
  if (![west, east, south, north].every(Number.isFinite)) return null;
  if (west >= east || south >= north) return null;
  if (west < -180 || east > 180 || south < -90 || north > 90) return null;
  return { west, east, south, north };
}


function applyWorldFileToGeoraster(georaster, affine) {
  if (!georaster || !affine) return false;
  const width = Number(georaster?.width);
  const height = Number(georaster?.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;
  const [A, D, B, E, C, F] = affine.map((n) => Number(n));
  if (![A, B, C, D, E, F].every((n) => Number.isFinite(n))) return false;

  const mapCorner = (col, row) => ([
    (A * col) + (B * row) + C,
    (D * col) + (E * row) + F,
  ]);
  const corners = [
    mapCorner(-0.5, -0.5),
    mapCorner(width - 0.5, -0.5),
    mapCorner(-0.5, height - 0.5),
    mapCorner(width - 0.5, height - 0.5),
  ];
  const xs = corners.map((pt) => Number(pt?.[0])).filter((n) => Number.isFinite(n));
  const ys = corners.map((pt) => Number(pt?.[1])).filter((n) => Number.isFinite(n));
  if (xs.length !== 4 || ys.length !== 4) return false;

  georaster.xmin = Math.min(...xs);
  georaster.xmax = Math.max(...xs);
  georaster.ymin = Math.min(...ys);
  georaster.ymax = Math.max(...ys);
  if (Math.abs(B) < 1e-12 && Math.abs(D) < 1e-12) {
    georaster.pixelWidth = Math.abs(A);
    georaster.pixelHeight = Math.abs(E);
  }
  return true;
}

function buildGeoTiffColorFn(georaster, options = {}) {
  const nodataSet = new Set();
  const pushNoData = (v) => {
    const n = Number(v);
    if (Number.isFinite(n)) nodataSet.add(n);
  };

  pushNoData(georaster?.noDataValue);
  (Array.isArray(options?.nodataValues) ? options.nodataValues : []).forEach(pushNoData);
  if (options?.nodataValue !== undefined) pushNoData(options.nodataValue);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const asByte = (v) => clamp(Math.round(Number(v) || 0), 0, 255);
  const mins = Array.isArray(georaster?.mins) ? georaster.mins : [];
  const maxs = Array.isArray(georaster?.maxs) ? georaster.maxs : [];
  const rampName = String(options?.colorRamp || "viridis").toLowerCase();
  const stretchMinOpt = Number(options?.stretchMin);
  const stretchMaxOpt = Number(options?.stretchMax);
  const ramp = getSimpleRamp(rampName);
  const stretchMin = Number.isFinite(stretchMinOpt) ? stretchMinOpt : (Number.isFinite(mins[0]) ? mins[0] : 0);
  const stretchMax = Number.isFinite(stretchMaxOpt) ? stretchMaxOpt : (Number.isFinite(maxs[0]) ? maxs[0] : 255);

  return (values) => {
    if (!Array.isArray(values) || !values.length) return null;
    const nums = values.map((v) => Number(v));
    if (nums.every((v) => !Number.isFinite(v))) return null;

    if (nums.length >= 3) {
      const rgb = nums.slice(0, 3);
      // For multiband rasters, only treat as nodata when all color bands are nodata.
      // This avoids dropping valid pixels where one channel can legitimately be 0.
      if (rgb.every((v) => nodataSet.has(v))) return null;
      const r = asByte(nums[0]);
      const g = asByte(nums[1]);
      const b = asByte(nums[2]);
      const a = nums.length >= 4 && Number.isFinite(nums[3]) ? clamp(Number(nums[3]) / 255, 0, 1) : 1;
      if (a <= 0) return null;
      return `rgba(${r},${g},${b},${a})`;
    }

    const v = nums[0];
    if (nodataSet.has(v)) return null;
    const span = Math.max(1e-9, stretchMax - stretchMin);
    const t = clamp((v - stretchMin) / span, 0, 1);
    const [r, g, b] = sampleRamp(ramp, t);
    return `rgba(${r},${g},${b},1)`;
  };
}

function getSimpleRamp(name) {
  const ramps = {
    grayscale: [[0, 0, 0], [255, 255, 255]],
    viridis: [[68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37]],
    plasma: [[13, 8, 135], [106, 0, 168], [177, 42, 144], [225, 100, 98], [252, 166, 54], [240, 249, 33]],
    inferno: [[0, 0, 4], [66, 10, 104], [147, 38, 103], [221, 81, 58], [252, 165, 10], [252, 255, 164]],
    spectral: [[94, 79, 162], [50, 136, 189], [102, 194, 165], [230, 245, 152], [253, 174, 97], [213, 62, 79]],
    terrain: [[51, 102, 0], [153, 204, 102], [230, 220, 170], [170, 130, 90], [255, 255, 255]],
    copper: [[30, 20, 10], [130, 80, 40], [230, 150, 90], [255, 220, 180]],
    blue_orange: [[20, 60, 160], [90, 160, 230], [240, 170, 70], [170, 80, 20]],
  };
  return ramps[name] || ramps.viridis;
}

function sampleRamp(ramp, t) {
  const pts = Array.isArray(ramp) && ramp.length >= 2 ? ramp : [[0, 0, 0], [255, 255, 255]];
  const clamped = Math.max(0, Math.min(1, Number(t) || 0));
  const scaled = clamped * (pts.length - 1);
  const i0 = Math.floor(scaled);
  const i1 = Math.min(pts.length - 1, i0 + 1);
  const f = scaled - i0;
  const c0 = pts[i0];
  const c1 = pts[i1];
  const lerp = (a, b) => Math.round((Number(a) || 0) + ((Number(b) || 0) - (Number(a) || 0)) * f);
  return [lerp(c0[0], c1[0]), lerp(c0[1], c1[1]), lerp(c0[2], c1[2])];
}

function buildEsriFeatureStyle(feature, opacity, styleBuilder) {
  const base = typeof styleBuilder === "function" ? styleBuilder(feature) : {};
  const weight = Number.isFinite(Number(base?.weight)) ? Number(base.weight) : 1;
  const fillOpacity = Number.isFinite(Number(base?.fillOpacity)) ? Number(base.fillOpacity) : 0.35;
  const lineOpacity = Number.isFinite(Number(base?.opacity)) ? Number(base.opacity) : 0.9;
  const alpha = Math.max(0, Math.min(1, Number(opacity) || 0));
  return {
    color: base?.color || "#4b5563",
    weight,
    opacity: lineOpacity * alpha,
    fillColor: base?.fillColor || base?.color || "#93c5fd",
    fillOpacity: fillOpacity * alpha,
    dashArray: base?.dashArray || null,
  };
}

function applyEsriFeatureOpacity(rec) {
  if (!rec?.leaflet?.setStyle) return;
  const styleBuilder = typeof rec?.options?.styleBuilder === "function"
    ? rec.options.styleBuilder
    : null;
  rec.leaflet.setStyle((feature) => buildEsriFeatureStyle(feature, rec.opacity, styleBuilder));
}
